import { useEffect, useState, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, X, Send } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import { toast } from 'sonner';

export default function QuotationViewer({ quotation, open, onClose }) {
  const { token } = useContext(AuthContext);
  const [companySettings, setCompanySettings] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [footerContent, setFooterContent] = useState(null);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (open) {
      fetchCompanySettings();
      fetchInvoiceSettings();
      fetchFooterContent();
    }
  }, [open]);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to load company settings');
    }
  };

  const fetchFooterContent = async () => {
    try {
      const response = await axios.get(`${API}/website/content/footer`);
      setFooterContent(response.data.content);
    } catch (error) {
      console.error('Failed to load footer content');
    }
  };

  const fetchInvoiceSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/invoice-quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceSettings(response.data);
    } catch (error) {
      console.error('Failed to load invoice settings');
    }
  };

  const handleDownload = async () => {
    const loadingToast = toast.loading('Generating PDF...');
    console.log('[PDF] Starting Quotation PDF download...');
    
    try {
      const element = printRef.current;
      
      if (!element) {
        throw new Error('Quotation content not found');
      }

      // Store original styles
      const originalPadding = element.style.padding;
      const originalMaxWidth = element.style.maxWidth;
      const originalMargin = element.style.margin;
      const originalWidth = element.style.width;
      
      // Temporarily adjust for PDF capture - full width, minimal padding
      element.style.padding = '10px';
      element.style.maxWidth = 'none';
      element.style.margin = '0';
      element.style.width = '210mm'; // A4 width

      // Wait for style changes and images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        windowWidth: 794, // A4 width in pixels at 96 DPI
        width: element.offsetWidth
      });

      // Restore original styles
      element.style.padding = originalPadding;
      element.style.maxWidth = originalMaxWidth;
      element.style.margin = originalMargin;
      element.style.width = originalWidth;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Minimal margins
      const margin = 5;
      const contentWidth = pdfWidth - (2 * margin);
      const contentHeight = pdfHeight - (2 * margin);
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let pageCount = 0;

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Add pages with minimal margins
      while (heightLeft > 0 || pageCount === 0) {
        if (pageCount > 0) {
          pdf.addPage();
        }
        
        const yPosition = pageCount === 0 ? margin : margin - (pageCount * contentHeight);
        pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);
        
        heightLeft -= contentHeight;
        pageCount++;
        
        if (heightLeft <= 0) break;
      }

      // Generate blob
      let blob = pdf.output('blob');
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Generated PDF blob is empty');
      }

      // Ensure correct MIME type
      if (blob.type !== 'application/pdf') {
        blob = new Blob([await blob.arrayBuffer()], { type: 'application/pdf' });
      }

      // Create object URL
      const url = URL.createObjectURL(blob);

      // Create download link with proper attributes
      const filename = `quotation-${quotation.quotation_number}.pdf`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);

      // Trigger download with MouseEvent (more reliable)
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(event);

      // Cleanup after 1.5 seconds
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('Failed to revoke object URL:', e);
        }
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }, 1500);

      console.log(`[PDF] Download triggered: ${filename} (${blob.size} bytes)`);
      
      toast.dismiss(loadingToast);
      toast.success('Quotation PDF downloaded!');
      
    } catch (error) {
      console.error('[PDF] Download failed:', error);
      toast.dismiss(loadingToast);
      toast.error(`Failed: ${error.message}`);
    }
  };

  const handleSend = async (platform) => {
    setShowSendOptions(false);
    toast.info('Generating PDF...');
    
    const element = printRef.current;
    const opt = {
      margin: 0.5,
      filename: `quotation-${quotation.quotation_number}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    try {
      const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      const file = new File([pdfBlob], `quotation-${quotation.quotation_number}.pdf`, { type: 'application/pdf' });

      const storePhone = companySettings?.store_handler_phone || '';
      const phoneNumber = storePhone.replace(/[^0-9]/g, '');

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Quotation',
          text: `Quotation ${quotation.quotation_number} for ${quotation.customer_name}`,
          files: [file]
        });
        toast.success('Shared successfully!');
      } else {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quotation-${quotation.quotation_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        const message = `Quotation ${quotation.quotation_number}\nCustomer: ${quotation.customer_name}\nTotal: ${formatCurrency(quotation.total)}`;
        
        if (platform === 'whatsapp') {
          toast.success('PDF downloaded! Opening WhatsApp...');
          setTimeout(() => {
            window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message + '\n\nPlease attach the downloaded PDF file.')}`, '_blank');
          }, 500);
        } else if (platform === 'viber') {
          const formattedPhone = storePhone.startsWith('+') ? storePhone : `+${phoneNumber}`;
          toast.success('PDF downloaded! Opening Viber...');
          if (navigator.clipboard) {
            navigator.clipboard.writeText(message + '\n\nPlease attach the downloaded PDF file.');
          }
          setTimeout(() => {
            window.open(`viber://chat?number=${encodeURIComponent(formattedPhone)}`, '_blank');
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share. Please try downloading instead.');
    }
  };

  if (!quotation) return null;

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const expiryDate = quotation.expires_at ? new Date(quotation.expires_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Quotation Preview</DialogTitle>
              <div className="flex gap-2">
                <Button onClick={handleDownload} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button onClick={() => setShowSendOptions(true)} size="sm" variant="outline">
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div ref={printRef} id="quotation-document" className="bg-white p-4 sm:p-8 print:p-8">
            {/* Header with custom text if available */}
            {companySettings?.document_header && (
              <div className="mb-4 text-center text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: companySettings.document_header }} />
            )}
            
            {/* Header - Logo LEFT, Info RIGHT */}
            <div className="flex justify-between items-start mb-6 sm:mb-8 pb-4 border-b-2 border-slate-300">
              <div className="flex-1">
                {companySettings?.company_logo && (
                  <img 
                    src={companySettings.company_logo} 
                    alt="Company Logo" 
                    className="object-contain" 
                    style={{ height: `${companySettings?.logo_size || 96}px` }}
                  />
                )}
              </div>
              <div className="text-right">
                <h2 className="text-2xl sm:text-3xl font-bold text-emerald-600 mb-2">QUOTATION</h2>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Number:</span> {quotation.quotation_number}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold">Date:</span> {today}
                </p>
                {expiryDate && (
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold">Valid Until:</span> {expiryDate}
                  </p>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-slate-50 rounded-lg">
              <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">BILL TO:</h3>
              <p className="font-semibold text-slate-900 text-sm sm:text-base break-words">{quotation.customer_name}</p>
              {quotation.customer_email && (
                <p className="text-xs sm:text-sm text-slate-600 break-all">{quotation.customer_email}</p>
              )}
              {quotation.customer_phone && (
                <p className="text-xs sm:text-sm text-slate-600">{quotation.customer_phone}</p>
              )}
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto mb-6 sm:mb-8">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-2 sm:py-3 text-xs sm:text-sm font-semibold text-slate-700">DESCRIPTION</th>
                    <th className="text-center py-2 sm:py-3 text-xs sm:text-sm font-semibold text-slate-700 w-16">QTY</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-semibold text-slate-700 w-24">PRICE</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-semibold text-slate-700 w-28">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.items.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-200">
                      <td className="py-2 sm:py-3 text-slate-900">
                        <div className="font-medium text-xs sm:text-sm break-words">{item.product_name}</div>
                        {item.description && item.description !== item.product_name && (
                          <div className="text-xs text-slate-600 break-words">{item.description}</div>
                        )}
                      </td>
                      <td className="py-2 sm:py-3 text-center text-slate-700 text-xs sm:text-sm">{item.quantity}</td>
                      <td className="py-2 sm:py-3 text-right text-slate-700 text-xs sm:text-sm whitespace-nowrap">{formatCurrency(item.price)}</td>
                      <td className="py-2 sm:py-3 text-right font-medium text-slate-900 text-xs sm:text-sm whitespace-nowrap">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-6 sm:mb-8">
              <div className="w-full sm:w-64">
                <div className="flex justify-between py-2 text-slate-700 text-xs sm:text-sm">
                  <span>Subtotal:</span>
                  <span className="whitespace-nowrap">{formatCurrency(quotation.subtotal)}</span>
                </div>
                {quotation.discount > 0 && (
                  <div className="flex justify-between py-2 text-slate-700 text-xs sm:text-sm">
                    <span>Discount:</span>
                    <span className="text-red-600 whitespace-nowrap">-{formatCurrency(quotation.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 text-slate-700 text-xs sm:text-sm">
                  <span>Tax:</span>
                  <span className="whitespace-nowrap">{formatCurrency(quotation.tax)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-slate-300 text-base sm:text-lg font-bold text-slate-900">
                  <span>TOTAL:</span>
                  <span className="text-emerald-600 whitespace-nowrap">{formatCurrency(quotation.total)}</span>
                </div>
              </div>
            </div>

            {/* Terms & Conditions and Payment Methods - NEW LAYOUT */}
            {invoiceSettings && (invoiceSettings.payment_banks?.length > 0 || invoiceSettings.terms_and_conditions_quotation) && (
              <div className="mb-6">
                {/* Terms and Conditions ABOVE */}
                {invoiceSettings.terms_and_conditions_quotation && (
                  <div className="mb-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-2">TERMS & CONDITIONS</h3>
                    <div className="text-xs text-slate-600 whitespace-pre-wrap">
                      {invoiceSettings.terms_and_conditions_quotation}
                    </div>
                  </div>
                )}

                {/* Payment Methods BELOW - Each Bank in One Row */}
                {invoiceSettings.payment_banks?.length > 0 && (
                  <div className="space-y-3">
                    {invoiceSettings.payment_banks.map((bank, index) => (
                      <div key={bank.id || index} className="flex items-center gap-6">
                        {/* Bank Logo */}
                        {bank.bank_logo && (
                          <img 
                            src={bank.bank_logo} 
                            alt={bank.bank_name} 
                            className="h-8 w-auto object-contain flex-shrink-0"
                          />
                        )}
                        
                        {/* Account Numbers in Single Row */}
                        <div className="flex gap-8 text-xs">
                          {bank.accounts?.filter(acc => acc.account_number).map((account, accIndex) => (
                            <div key={accIndex} className="flex gap-2">
                              <span className="font-semibold text-slate-700">{account.currency}</span>
                              <span className="text-slate-900 font-mono">{account.account_number}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Customer Notes */}
            {quotation.customer_notes && (
              <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <h3 className="text-sm font-semibold text-blue-700 mb-2">SPECIAL NOTES FOR CUSTOMER:</h3>
                <p className="text-sm text-slate-700">{quotation.customer_notes}</p>
              </div>
            )}

            {/* Internal Notes */}
            {quotation.notes && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">INTERNAL NOTES:</h3>
                <p className="text-sm text-slate-600">{quotation.notes}</p>
              </div>
            )}

            {/* Footer with Partner Logos */}
            <div className="border-t-2 border-slate-300 pt-4 mt-8">
              {companySettings?.document_footer && (
                <div className="text-center text-sm text-slate-600 mb-3" dangerouslySetInnerHTML={{ __html: companySettings.document_footer }} />
              )}
              
              {/* Partner Logos on Left and Right */}
              {footerContent?.partner_logos && footerContent.partner_logos.length > 0 && (
                <div className="flex justify-between items-center mb-3 px-4">
                  {/* Left Side Logos */}
                  <div className="flex gap-3 items-center">
                    {footerContent.partner_logos.slice(0, Math.ceil(footerContent.partner_logos.length / 2)).map((logo, index) => (
                      <img 
                        key={index}
                        src={logo} 
                        alt={`Partner ${index + 1}`} 
                        className="h-6 w-auto object-contain opacity-70"
                      />
                    ))}
                  </div>
                  
                  {/* Right Side Logos */}
                  <div className="flex gap-3 items-center">
                    {footerContent.partner_logos.slice(Math.ceil(footerContent.partner_logos.length / 2)).map((logo, index) => (
                      <img 
                        key={index}
                        src={logo} 
                        alt={`Partner ${index + Math.ceil(footerContent.partner_logos.length / 2) + 1}`} 
                        className="h-6 w-auto object-contain opacity-70"
                      />
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-center text-sm text-slate-500">
                <p>Thank you for your business!</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #quotation-document,
          #quotation-document * {
            visibility: visible;
          }
          #quotation-document {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button {
            display: none !important;
          }
        }
      `}</style>

      {/* Send Options Dialog */}
      <Dialog open={showSendOptions} onOpenChange={setShowSendOptions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Quotation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <p className="text-slate-600 text-sm">
              PDF will be generated and shared via your chosen platform:
            </p>
            
            <Button
              onClick={() => handleSend('whatsapp')}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Send PDF via WhatsApp
            </Button>
            
            <Button
              onClick={() => handleSend('viber')}
              className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Send PDF via Viber
            </Button>
            
            <p className="text-xs text-slate-500">
              💡 PDF will be downloaded. On mobile, you can share it directly. On desktop, attach the downloaded file manually.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
